import { Markdown } from "@/components/ui/markdown";
import { type ProjectWikiRef, ProjectWikisAside } from "@/components/wiki/project-wikis-aside";
import { WikiSidebar } from "@/components/wiki/wiki-sidebar";
import { api } from "@/lib/api";
import { preprocessWikiMarkdown } from "@/lib/wiki-markdown";
import { Link, createFileRoute } from "@tanstack/react-router";

/**
 * `/wiki` — the company wiki home / index, rendered inside Hub the
 * same way the project wiki tab is. The wiki lives in a single
 * GitLab project (`COMPANY_WIKI_PROJECT`); Hub only renders it.
 *
 * GitLab convention: `home` is the landing page; the server falls
 * back to the first page when `home` is absent. A right-hand column
 * links out to every project's own wiki.
 */

type WikiPageSummary = { slug: string; title: string };

type WikiHome = { slug: string; title: string; content: string };

type WikiResponse = {
  pages: WikiPageSummary[];
  home: WikiHome | null;
  sidebarMarkdown: string | null;
  loadError: string | null;
};

type ProjectsResponse = { projects: ProjectWikiRef[] };

export const Route = createFileRoute("/_app/wiki/")({
  loader: async () => {
    const [wiki, projectsRes] = await Promise.all([
      api.get<WikiResponse>("/api/wiki"),
      api.get<ProjectsResponse>("/api/projects?pageSize=200"),
    ]);
    return { wiki, projects: projectsRes.projects };
  },
  component: CompanyWikiPage,
});

function CompanyWikiPage() {
  const { wiki, projects } = Route.useLoaderData();
  const { pages, home, sidebarMarkdown, loadError } = wiki;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-heading text-2xl font-bold">Netlion Wiki</h1>
        <p className="text-muted-foreground text-sm mt-1">
          The company knowledge base — processes, onboarding and the tools we use day to day.
        </p>
      </div>

      <div className="flex flex-col gap-6 lg:flex-row">
        <WikiSidebar pages={pages} sidebarMarkdown={sidebarMarkdown} />

        <div className="min-w-0 flex-1">
          <h2 className="font-heading text-xl font-bold mb-4">{home ? home.title : "Wiki"}</h2>

          {loadError ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
              Couldn't load the wiki from GitLab: {loadError}
            </div>
          ) : home ? (
            <div className="rounded-md border bg-card p-6">
              <Markdown content={preprocessWikiMarkdown(home.content, "/wiki")} />
            </div>
          ) : pages.length > 0 ? (
            <div className="rounded-md border bg-card divide-y">
              {pages.map((p) => (
                <div key={p.slug} className="flex items-center justify-between px-4 py-3">
                  <Link
                    to="/wiki/$"
                    params={{ _splat: p.slug }}
                    className="font-medium hover:underline text-primary"
                  >
                    {p.title}
                  </Link>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-md border bg-card p-12 text-center">
              <p className="text-muted-foreground">No wiki pages yet.</p>
            </div>
          )}
        </div>

        <ProjectWikisAside projects={projects} />
      </div>
    </div>
  );
}
